/**
 * COLONY COMMAND — the meta tree, resolved from `src/content/meta-tree.json`
 * (doc 08). Code never retypes a doc number; this module only shapes the file
 * into types and answers "what does owning these ids mean".
 *
 * Nodes with `available: false` are authored but not in the water yet — their
 * towers and abilities arrive with the stage-5 content pass. They render locked
 * with a reason rather than vanishing, so the tree's shape is honest.
 */

import treeJson from '../content/meta-tree.json' with { type: 'json' };
import type { Resources } from '../sim/types.ts';

export interface NodeGrants {
  tower?: string;
  ability?: string;
  building?: string;
  coreHpMult?: number;
  structureHpMult?: number;
  storageMult?: number;
  kit?: Partial<Resources>;
  doubleReelBonus?: number;
  shoalCounts?: boolean;
  codex?: boolean;
  rangeOverlay?: boolean;
}

export interface MetaNode {
  id: string;
  name: string;
  cost: number;
  effect: string;
  available: boolean;
  grants: NodeGrants;
  /** Branch id, filled in here so a node always knows where it lives. */
  branch: string;
}

export interface MetaBranch {
  id: string;
  name: string;
  blurb: string;
  nodes: MetaNode[];
}

interface TreeFile {
  pearls: {
    shoalCleared: number;
    minibossKilled: number;
    bossKilled: number;
    riledBonus: number;
    bossSkipBonus: number;
    bossSkipBonusMax: number;
    firstClearMult: number;
    codexFirstKill: number;
    pressureTierBonus: number;
  };
  towerXp: {
    perDamage: number;
    perWave: number;
    firstDepthClear: number;
    catchUpBonus: number;
    levelThresholds: number[];
  };
  assist: { coreHpBonus: number; perLossBonus: number; maxBonus: number };
  firstLoss: Array<{
    loss: number;
    id: string;
    headline: string;
    text: string;
    minPearls?: number;
    grantsNode?: string;
    discountNode?: string;
    discountTo?: number;
  }>;
  branches: Array<{ id: string; name: string; blurb: string; nodes: Omit<MetaNode, 'branch'>[] }>;
}

const FILE = treeJson as unknown as TreeFile;

export const PEARL_RATES = FILE.pearls;
export const XP_RATES = FILE.towerXp;
export const ASSIST = FILE.assist;
export const FIRST_LOSS = FILE.firstLoss;

export const BRANCHES: MetaBranch[] = FILE.branches.map((b) => ({
  id: b.id,
  name: b.name,
  blurb: b.blurb,
  nodes: b.nodes.map((n) => ({ ...n, branch: b.id })),
}));

export const NODES: Record<string, MetaNode> = Object.fromEntries(
  BRANCHES.flatMap((b) => b.nodes.map((n) => [n.id, n] as const)),
);

export function node(id: string): MetaNode | undefined {
  return NODES[id];
}

/**
 * Price after the first-loss schedule's discounts (doc 08: loss #3 halves the
 * Fish Whisperer). Reveals are recorded on the profile, so the discount is
 * permanent once earned.
 */
export function costOf(id: string, reveals: string[]): number {
  const n = NODES[id];
  if (!n) return Infinity;
  for (const step of FIRST_LOSS) {
    if (step.discountNode === id && step.discountTo !== undefined && reveals.includes(step.id)) {
      return step.discountTo;
    }
  }
  return n.cost;
}

/** Every Pearl in the tree, at list price — the "you own X of Y" denominator. */
export const TREE_TOTAL_COST = Object.values(NODES).reduce((sum, n) => sum + n.cost, 0);
